import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Alert,
  Button,
  DateField,
  Field,
  Modal,
  ModalActions,
  Select,
  Switch,
  TextArea,
} from '@evogard/ui';
import type { ChargeMember, RevenueBeneficiary } from '@evogard/contract';
import {
  REVENUE_DESCRIPTION_MAX,
  REVENUE_NOTES_MAX,
  businessDate,
  parseMoneyInput,
} from '@evogard/domain';
import { api, ok } from '../../../lib/api.js';
import { useDebouncedTerm } from '../../../lib/debounce.js';
import { useSessionContext } from '../../../lib/session.js';
import { REVENUES_KEY } from './RevenuesScreen.js';

type PayerKind = 'associado' | 'beneficiario';

/** Quem foi escolhido: o associado da procura ou o beneficiário da plataforma. */
type Payer =
  | { kind: 'associado'; member: ChargeMember }
  | { kind: 'beneficiario'; beneficiary: RevenueBeneficiary };

/**
 * Lançar uma receita avulsa (29/10/2026) — só o Financeiro lança à mão.
 *
 * Quem paga é o associado ou um beneficiário da plataforma. A cobrança no
 * Asaas é opcional, e só para o associado: o beneficiário recebe a baixa à
 * mão, com comprovante, quando o dinheiro entrar.
 */
export function RevenueFormModal({
  open,
  onOpenChange,
  onLaunched,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLaunched: (id: number) => void;
}) {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  const client = useQueryClient();
  const today = businessDate(new Date());

  const [kind, setKind] = useState<PayerKind>('associado');
  const [term, setTerm] = useState('');
  const [payer, setPayer] = useState<Payer | null>(null);
  const [amount, setAmount] = useState('');
  const [dueDate, setDueDate] = useState(today);
  const [competencyDate, setCompetencyDate] = useState(today);
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [charge, setCharge] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounced = useDebouncedTerm(term.trim());

  const categories = useQuery({
    queryKey: [...REVENUES_KEY, tenantId, 'categorias'],
    queryFn: async () =>
      ok(
        await api.revenues.revenueCategories(),
        200,
        'Não foi possível carregar as categorias.',
      ),
    enabled: open && tenantId !== undefined,
  });

  const search = useQuery({
    queryKey: [...REVENUES_KEY, tenantId, 'pagadores', kind, debounced],
    queryFn: async () =>
      kind === 'associado'
        ? ok(
            await api.charges.findChargeMembers({ body: { query: debounced } }),
            200,
            'Não foi possível procurar o associado.',
          ).items.map((m) => ({ kind: 'associado' as const, member: m }))
        : ok(
            await api.revenues.findRevenueBeneficiaries({ body: { query: debounced } }),
            200,
            'Não foi possível procurar o beneficiário.',
          ).items.map((b) => ({ kind: 'beneficiario' as const, beneficiary: b })),
    enabled: open && payer === null && debounced.length >= 3,
  });

  const reset = () => {
    setKind('associado');
    setTerm('');
    setPayer(null);
    setAmount('');
    setDueDate(today);
    setCompetencyDate(today);
    setCategory('');
    setDescription('');
    setNotes('');
    setCharge(true);
    setSubmitted(false);
    setError(null);
  };
  const close = () => {
    reset();
    onOpenChange(false);
  };

  const money = parseMoneyInput(amount);
  const problems = {
    payer: payer
      ? null
      : kind === 'associado'
        ? 'Escolha o associado.'
        : 'Escolha o beneficiário.',
    amount: money !== null && money > 0 ? null : 'Informe um valor maior que zero.',
    dueDate: dueDate ? null : 'Informe o vencimento.',
    competencyDate: competencyDate ? null : 'Informe a competência.',
    category: category ? null : 'Escolha a categoria.',
    description: description.trim() ? null : 'Escreva a descrição.',
  };
  const valid = Object.values(problems).every((p) => p === null);
  const willCharge = kind === 'associado' && charge;
  const noDocument = payer?.kind === 'associado' && !payer.member.hasDocument;

  const launch = useMutation({
    mutationFn: async () =>
      ok(
        await api.revenues.createRevenue({
          body: {
            payer:
              payer!.kind === 'associado'
                ? { kind: 'associado', memberId: payer!.member.id }
                : { kind: 'beneficiario', beneficiaryId: payer!.beneficiary.id },
            amount,
            dueDate,
            competencyDate,
            category,
            description: description.trim(),
            notes: notes.trim() || null,
            charge: willCharge,
          },
        }),
        201,
        'Não foi possível lançar a receita.',
      ),
    onSuccess: (revenue) => {
      void client.invalidateQueries({ queryKey: [...REVENUES_KEY, tenantId] });
      reset();
      onLaunched(revenue.id);
    },
    onError: (e: Error) => setError(e.message),
  });

  const fieldError = (key: keyof typeof problems) => (submitted ? problems[key] : null);

  return (
    <Modal
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      title="Lançar receita"
      size="lg"
      descricao="Uma receita avulsa: o que a casa tem a receber fora dos processos dos departamentos."
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          setSubmitted(true);
          if (valid && !(willCharge && noDocument)) launch.mutate();
        }}
      >
        {error && <Alert tone="erro">{error}</Alert>}

        <Select
          label="Quem paga"
          value={kind}
          onChange={(e) => {
            setKind(e.target.value as PayerKind);
            setPayer(null);
            setTerm('');
          }}
        >
          <option value="associado">Associado</option>
          <option value="beneficiario">Beneficiário cadastrado na plataforma</option>
        </Select>

        {payer ? (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-borda p-3">
            <span className="flex flex-col">
              <span className="text-texto">
                {payer.kind === 'associado'
                  ? (payer.member.name ?? 'Sem nome')
                  : payer.beneficiary.name}
              </span>
              <span className="text-sm text-texto-suave">
                {payer.kind === 'associado'
                  ? [payer.member.code, payer.member.documentMasked]
                      .filter(Boolean)
                      .join(' · ')
                  : (payer.beneficiary.documentMasked ?? 'Sem documento')}
              </span>
            </span>
            <Button
              type="button"
              variant="discreto"
              size="sm"
              onClick={() => setPayer(null)}
            >
              Trocar
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <Field
              label={kind === 'associado' ? 'Associado' : 'Beneficiário'}
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              autoComplete="off"
              placeholder={
                kind === 'associado'
                  ? 'Código, CPF ou nome'
                  : 'Nome do titular ou documento'
              }
              error={fieldError('payer')}
              help="Digite ao menos 3 caracteres."
            />
            {search.isError && <Alert tone="erro">{search.error.message}</Alert>}
            {search.data && search.data.length === 0 && (
              <p className="text-sm text-texto-suave">
                {kind === 'associado'
                  ? 'Nenhum associado encontrado nesta base.'
                  : 'Nenhum beneficiário encontrado nesta base. Ele é cadastrado em Plataforma › Cadastros › Contas de pagamento.'}
              </p>
            )}
            {search.data && search.data.length > 0 && (
              <ul className="flex flex-col divide-y divide-borda rounded-md border border-borda">
                {search.data.map((p) => (
                  <li
                    key={
                      p.kind === 'associado' ? `a${p.member.id}` : `b${p.beneficiary.id}`
                    }
                  >
                    <button
                      type="button"
                      className="flex w-full flex-col items-start px-3 py-2 text-left hover:bg-superficie-suave focus-visible:outline-2"
                      onClick={() => setPayer(p)}
                    >
                      <span className="text-texto">
                        {p.kind === 'associado'
                          ? (p.member.name ?? 'Sem nome')
                          : p.beneficiary.name}
                      </span>
                      <span className="text-sm text-texto-suave">
                        {p.kind === 'associado'
                          ? `${p.member.code} · ${p.member.documentMasked ?? 'sem documento'}`
                          : (p.beneficiary.documentMasked ?? 'sem documento')}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-3">
          <Field
            label="Valor"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputMode="decimal"
            prefix="R$"
            placeholder="0,00"
            autoComplete="off"
            error={fieldError('amount')}
          />
          <DateField
            label="Vencimento"
            value={dueDate}
            onChange={setDueDate}
            layer="modal"
            error={fieldError('dueDate')}
          />
          <DateField
            label="Competência"
            value={competencyDate}
            onChange={setCompetencyDate}
            layer="modal"
            error={fieldError('competencyDate')}
          />
        </div>

        <Select
          label="Categoria"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          error={fieldError('category')}
          help="As categorias de receita do Conta Azul desta base."
        >
          <option value="">Escolha</option>
          {categories.data?.items.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        {categories.data && categories.data.items.length === 0 && (
          <Alert tone="alerta">
            Esta base não tem categorias de receita do Conta Azul cadastradas, e a receita
            não é lançada sem uma.
          </Alert>
        )}

        <Field
          label="Descrição"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={REVENUE_DESCRIPTION_MAX}
          error={fieldError('description')}
          help={
            willCharge ? 'Aparece para o associado na página de pagamento.' : undefined
          }
        />
        <TextArea
          label="Observações"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={REVENUE_NOTES_MAX}
          rows={2}
        />

        {kind === 'associado' ? (
          <div className="flex flex-col gap-2">
            <Switch
              label="Cobrar no Asaas"
              checked={charge}
              onCheckedChange={setCharge}
              onLabel="Cobrar no Asaas agora"
              offLabel="Só lançar"
            />
            {charge && noDocument && (
              <Alert
                tone="alerta"
                title="Este associado não tem CPF ou CNPJ no cadastro."
              >
                O Asaas exige um documento para cobrar. Lance sem cobrança, ou espere o
                documento chegar do SGA.
              </Alert>
            )}
          </div>
        ) : (
          <p className="text-sm text-texto-suave">
            O beneficiário não é cobrado no Asaas por enquanto. Quando o dinheiro entrar,
            dê a baixa na receita com o comprovante.
          </p>
        )}

        <ModalActions>
          <Button type="button" variant="contorno" onClick={close}>
            Cancelar
          </Button>
          <Button
            type="submit"
            loading={launch.isPending}
            disabled={willCharge && noDocument}
          >
            {willCharge ? 'Lançar e cobrar' : 'Lançar receita'}
          </Button>
        </ModalActions>
      </form>
    </Modal>
  );
}
